import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { UserPlus, XCircle, CheckCircle, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSIGNABLE_ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full system access' },
  { value: 'sales_manager', label: 'Sales Manager', description: 'Manage team and leads' },
  { value: 'account_executive', label: 'Account Executive', description: 'Own leads only' },
  { value: 'finance', label: 'Finance', description: 'Payments and installments' },
  { value: 'top_management', label: 'Top Management', description: 'Read-only executive view' },
];

export default function UserRequestsPage() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('account_executive');
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const { hasPermission, role } = usePermissions();
  const canApprove = hasPermission('users_approve');
  const canAssignAdmin = hasPermission('users_assign_admin');
  const isOwner = role === 'owner';



  useEffect(() => {
    fetchPendingUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/users/pending`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingUsers(data);
      } else if (response.status === 403) {
        toast.error('Access denied');
      }
    } catch (error) {
      logger.error('Failed to fetch pending users:', error);
      toast.error('Failed to load pending users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedUser || !selectedRole) return;
    
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/users/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          action: 'approve',
          role: selectedRole
        })
      });
      
      if (response.ok) {
        toast.success(`${selectedUser.name} approved as ${selectedRole.replace('_', ' ')}`);
        setIsApproveDialogOpen(false);
        setSelectedUser(null);
        fetchPendingUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve user');
      }
    } catch (error) {
      logger.error('Approve error:', error);
      toast.error('Failed to approve user');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDisable = async () => {
    if (!selectedUser) return;
    
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/users/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          action: 'disable'
        })
      });
      
      if (response.ok) {
        toast.success(`${selectedUser.name} has been disabled`);
        setIsDisableDialogOpen(false);
        setSelectedUser(null);
        fetchPendingUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to disable user');
      }
    } catch (error) {
      logger.error('Disable error:', error);
      toast.error('Failed to disable user');
    } finally {
      setIsProcessing(false);
    }
  };

  const openApproveDialog = (user) => {
    setSelectedUser(user);
    setSelectedRole('account_executive');
    setIsApproveDialogOpen(true);
  };

  const openDisableDialog = (user) => {
    setSelectedUser(user);
    setIsDisableDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!canApprove) {
    return (
      <div className="space-y-6" data-testid="user-requests-page">
        <h1 className="text-2xl font-heading font-semibold text-slate-900">User Requests</h1>
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You do not have permission to approve user requests.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">User Requests</h1>
          <p className="text-sm text-slate-500 mt-1">Review and approve pending user registrations</p>
        </div>
        <Badge variant="outline" className="text-amber-600 border-amber-300">
          <Clock className="w-3 h-3 mr-1" />
          {pendingUsers.length} Pending
        </Badge>
      </div>

      {!isOwner && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Limited Permissions</AlertTitle>
          <AlertDescription>
            As an Admin, you can approve users with roles except Admin. Only the Owner can assign Admin role.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Pending Approvals
          </CardTitle>
          <CardDescription>
            Users who have signed up and are waiting for approval
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : pendingUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No pending user requests at this time.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDisableDialog(user)}
                        data-testid={`disable-${user.user_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openApproveDialog(user)}
                        data-testid={`approve-${user.user_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={isApproveDialogOpen} onOpenChange={setIsApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve User</DialogTitle>
            <DialogDescription>
              Select a role for {selectedUser?.name} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Assign Role</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNABLE_ROLES.map((role) => {
                    const isDisabled = role.value === 'admin' && !canAssignAdmin;
                    return (
                      <SelectItem 
                        key={role.value} 
                        value={role.value}
                        disabled={isDisabled}
                      >
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-slate-500">{role.description}</span>
                          {isDisabled && <span className="text-xs text-red-500">(Owner only)</span>}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsApproveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={isDisableDialogOpen} onOpenChange={setIsDisableDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject User Request</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject {selectedUser?.name}'s access request? 
              They will be marked as disabled and won't be able to access the system.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDisableDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisable} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
